import React, { useState, useEffect } from 'react';
import { fetchDmsUsers, createDmsUser, deleteDmsUser, updateDmsUser } from '../api';

const DmsUserManagement = () => {
  const [authHeaders, setAuthHeaders] = useState(null);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ fullName: '', email: '', password: '' });

  // 1. Automatically Authenticate using LocalStorage
  useEffect(() => {
    const email = localStorage.getItem('adminUser');
    const pass = localStorage.getItem('adminPass');

    if (email && pass && email.trim().toLowerCase() === "nsfdc.it@gmail.com") {
      const headers = { 
        'Content-Type': 'application/json', 
        'x-admin-user': email.trim(), 
        'x-admin-pass': pass 
      };
      setAuthHeaders(headers);
      loadUsers(headers);
    }
  }, []);

  const loadUsers = async (headers) => {
    try {
      const res = await fetchDmsUsers(headers);
      setUsers(res.data);
    } catch (err) {
      alert("Failed to load users. Your session may have expired.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateDmsUser(editingUser.id, formData, authHeaders);
        alert("User updated successfully!");
      } else {
        await createDmsUser(formData, authHeaders);
        alert("User created successfully!");
      }
      setFormData({ fullName: '', email: '', password: '' });
      setEditingUser(null);
      loadUsers(authHeaders);
    } catch (err) {
      alert(err.response?.data?.message || "Operation failed.");
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    // When editing, password is left blank unless they want to change it
    setFormData({ fullName: user.fullName, email: user.email, password: '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you absolutely sure you want to permanently delete this user?")) return;
    try {
      await deleteDmsUser(id, authHeaders);
      loadUsers(authHeaders);
    } catch (err) {
      alert("Failed to delete user.");
    }
  };

  // If a normal admin somehow navigates to /dms-users via URL, block them
  if (!authHeaders) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <div className="alert alert-danger shadow-sm fw-bold">
          <i className="bi bi-exclamation-triangle-fill me-2"></i> 
          Unauthorized Access: You must be logged in as the Super Admin.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 px-4">
      <h4 className="fw-bold mb-4 text-primary">DMS User Management</h4>
      <div className="row">
        
        {/* ADD / EDIT FORM */}
        <div className="col-md-4">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-dark text-white fw-bold">
              {editingUser ? <><i className="bi bi-pencil-square me-2"></i>Edit DMS User</> : <><i className="bi bi-person-plus-fill me-2"></i>Create New DMS User</>}
            </div>
            <div className="card-body bg-light">
              <form onSubmit={handleSubmit}>
                <div className="mb-2">
                  <label className="small fw-bold">Full Name</label>
                  <input required className="form-control form-control-sm" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} />
                </div>
                <div className="mb-2">
                  <label className="small fw-bold">Email</label>
                  <input required type="email" className="form-control form-control-sm" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                <div className="mb-3">
                  <label className="small fw-bold">{editingUser ? 'New Password (Optional)' : 'Password'}</label>
                  <input type={editingUser ? "text" : "password"} className="form-control form-control-sm" onChange={e => setFormData({...formData, password: e.target.value})} />
                </div>
                
                {/* ACTION BUTTONS */}
                <button className="btn btn-success btn-sm w-100 fw-bold shadow-sm">
                  {editingUser ? 'Update User' : 'Add User'}
                </button>
                {editingUser && (
                  <button type="button" className="btn btn-outline-secondary btn-sm w-100 mt-2 fw-bold" onClick={() => {setEditingUser(null); setFormData({ fullName: '', email: '', password: '' })}}>
                    Cancel Edit
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* USERS TABLE WITH EXPLICIT BUTTONS */}
        <div className="col-md-8">
          <table className="table table-hover bg-white shadow-sm rounded border">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td className="align-middle fw-medium">{u.fullName}</td>
                  <td className="align-middle">{u.email}</td>
                  <td className="align-middle text-muted">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="text-center align-middle">
                    
                    {/* VISIBLE EDIT BUTTON */}
                    <button 
                      className="btn btn-sm btn-primary me-2 fw-bold shadow-sm" 
                      onClick={() => startEdit(u)}
                    >
                      <i className="bi bi-pencil-square me-1"></i> Edit
                    </button>

                    {/* VISIBLE DELETE BUTTON (Hidden for the Super Admin account to prevent self-deletion) */}
                    {u.email.toLowerCase() !== "nsfdc.it@gmail.com" ? (
                      <button 
                        className="btn btn-sm btn-danger fw-bold shadow-sm" 
                        onClick={() => handleDelete(u.id)}
                      >
                        <i className="bi bi-trash me-1"></i> Delete
                      </button>
                    ) : (
                      <span className="badge bg-secondary">Super Admin</span>
                    )}

                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-muted">
                    No DMS users created yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DmsUserManagement;